import {
    communicationBlockByStepId,
    communicationTypeForStep,
    readStepEmails,
    toOptionalIso,
} from './launchStepSources';

import type { LaunchConfigSnapshot, LaunchStepSnapshot } from './launchConfigSnapshot';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { CycleMilestone } from '@/types/cycle-evaluation/CycleMilestone';

import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { isLaunchCommunicationBlock } from '@/pages/CycleEvaluation/Communications/communicationBlocks';
import { LAUNCH_MILESTONE_BY_TYPE } from '@/pages/CycleEvaluation/Deadlines/milestones';

interface BuildCurrentSnapshotParams {
    group: CycleEvaluationGroup;
    orphanMilestones: CycleMilestone[];
    checkedTypes: EvaluationTypeValue[];
    cycleEvaluationByType: Partial<Record<EvaluationTypeValue, CycleEvaluation | undefined>>;
    templateLabelByType: Partial<Record<EvaluationTypeValue, string>>;
}

const buildCurrentSnapshot = ({
    group,
    orphanMilestones,
    checkedTypes,
    cycleEvaluationByType,
    templateLabelByType,
}: BuildCurrentSnapshotParams): LaunchConfigSnapshot => {
    const milestoneByType = new Map(
        orphanMilestones.map((milestone) => [milestone.milestoneType, milestone]),
    );
    const communicationByType = new Map(
        (group.communications ?? []).map((block) => [block.milestoneType, block]),
    );

    const steps: LaunchStepSnapshot[] = [];

    communicationBlockByStepId.forEach((definition) => {
        const isLaunchStep = isLaunchCommunicationBlock(definition);

        if (isLaunchStep && !checkedTypes.includes(definition.evaluationType)) {
            return;
        }

        const cycleEvaluation = isLaunchStep
            ? cycleEvaluationByType[definition.evaluationType]
            : undefined;
        const orphanMilestone = isLaunchStep
            ? undefined
            : milestoneByType.get(definition.milestoneType);
        const communicationType = communicationTypeForStep(definition);

        steps.push({
            id: definition.id,
            labelKey: definition.labelKey,
            scheduledAt: isLaunchStep
                ? toOptionalIso(cycleEvaluation?.cycleSchedule?.scheduledAt)
                : toOptionalIso(orphanMilestone?.scheduledAt),
            processedAt: isLaunchStep
                ? toOptionalIso(cycleEvaluation?.processedAt)
                : toOptionalIso(orphanMilestone?.processedAt),
            ...readStepEmails({
                definition,
                cycleEvaluation,
                block: communicationByType.get(communicationType),
            }),
        });
    });

    const evaluationsByType = Object.fromEntries(
        checkedTypes.map((reviewType) => [
            reviewType,
            {
                labelKey: LAUNCH_MILESTONE_BY_TYPE[reviewType].labelKey,
                isSelected: true,
                templateId:
                    cycleEvaluationByType[reviewType]?.cycleQuestionnaire?.reviewTemplateId ??
                    undefined,
                templateLabel: templateLabelByType[reviewType] ?? '',
                scheduledAt: toOptionalIso(
                    cycleEvaluationByType[reviewType]?.cycleSchedule?.scheduledAt,
                ),
            },
        ]),
    );

    return {
        name: group.name,
        cycleStart: toOptionalIso(group.evaluationPeriodStart),
        cycleEnd: toOptionalIso(group.evaluationPeriodEnd),
        audience: {
            isEntireCompany: group.audience?.cycleAudienceType === CycleAudienceType.ENTIRE_COMPANY,
            criteriaLabels: (group.audience?.cycleAudienceCriterias ?? []).map(
                (criteria) =>
                    `${criteria.criteriaType}:${criteria.customAttributeId ?? ''}:${criteria.value}`,
            ),
        },
        evaluationsByType,
        steps,
    };
};

export { buildCurrentSnapshot };
